import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { report } from "../../functions/api/guardian";
import { useAuth } from "./useAuth";
import { useAllUsers } from "./useUsers";

/**
 * Bewaakt of de data die het CMS nodig heeft ook daadwerkelijk binnenkomt, en
 * meldt het aan Guardian als dat niet zo is. Zie docs/monitoring.md.
 *
 * Twee signalen, want de bug die hiertoe leidde gaf er maar één:
 *
 * 1. **Een mislukte read.** redux-firestore legt listener-fouten in
 *    `firestoreReducer.errors.byQuery`; die zijn tot nu toe door niemand
 *    bekeken. Hier vangen we ze allemaal in één keer af, ook van queries die
 *    pas later worden toegevoegd.
 * 2. **Een lijst die klaar is met laden en tóch leeg is.** Dit is het signaal
 *    dat ontbrak: de klantenlijst wérd niet geweigerd op een manier die iemand
 *    zag, hij was gewoon leeg. Geen exception, wel kapot.
 *
 * Titels zijn bewust opgebouwd uit twee opsommingen (bron + foutcode) zodat
 * Guardian ze kan groeperen; alles wat per voorval verschilt gaat in `context`.
 */

/** Collecties die in een werkende omgeving nooit leeg zijn. */
const MOET_GEVULD_ZIJN = {
  products: "producten",
  orders: "orders",
  categories: "categorieën",
  cmsRoles: "rollen",
};

/** `orders?where=createdAt:>=:1788010893` -> `orders`. Houdt de titel stabiel. */
const basisnaam = (queryNaam) => String(queryNaam || "").split("?")[0];

export const useDataHealth = () => {
  const { isAuthenticated } = useAuth();
  const errorsByQuery = useSelector((state) => state.firestoreReducer.errors?.byQuery);
  const requesting = useSelector((state) => state.firestoreReducer.status?.requesting);
  const ordered = useSelector((state) => state.firestoreReducer.ordered);
  const users = useAllUsers();

  const gemeld = useRef(new Set());

  // 1. Mislukte reads.
  useEffect(() => {
    if (!errorsByQuery) return;
    Object.entries(errorsByQuery).forEach(([queryNaam, fout]) => {
      if (!fout) return;
      const bron = basisnaam(queryNaam);
      const code = fout.code || "onbekend";
      const sleutel = `read|${bron}|${code}`;
      if (gemeld.current.has(sleutel)) return;
      gemeld.current.add(sleutel);

      report({
        severity: "error",
        type: "firestore_read_failed",
        title: `Firestore-read mislukt (${code}): ${bron}`,
        context: { query: queryNaam, code, melding: fout.message },
      });
    });
  }, [errorsByQuery]);

  // 2. Klaar met laden en tóch leeg.
  useEffect(() => {
    if (!requesting || !ordered) return;
    Object.entries(MOET_GEVULD_ZIJN).forEach(([sleutelInStore, label]) => {
      // Alleen controleren wat deze gebruiker überhaupt ophaalt: wie geen
      // rechten op een collectie heeft, abonneert er niet op en hoort hier
      // geen melding over te krijgen.
      const isOpgevraagd = Object.entries(requesting).some(
        ([q, bezig]) => basisnaam(q) === sleutelInStore && bezig === false,
      );
      if (!isOpgevraagd) return;

      const lijst = ordered[sleutelInStore];
      if (!Array.isArray(lijst) || lijst.length > 0) return;

      const meldsleutel = `leeg|${sleutelInStore}`;
      if (gemeld.current.has(meldsleutel)) return;
      gemeld.current.add(meldsleutel);

      report({
        severity: "error",
        type: "empty_dataset",
        title: `Lijst onverwacht leeg: ${label}`,
        context: { bron: sleutelInStore },
      });
    });
  }, [requesting, ordered]);

  // 2b. Gebruikers lopen niet via redux-firestore (zie useUsers.js), dus apart.
  useEffect(() => {
    if (!isAuthenticated || !Array.isArray(users) || users.length > 0) return;
    const sleutel = "leeg|users";
    if (gemeld.current.has(sleutel)) return;
    gemeld.current.add(sleutel);

    report({
      severity: "error",
      type: "empty_dataset",
      title: "Lijst onverwacht leeg: gebruikers",
      context: { bron: "users" },
    });
  }, [isAuthenticated, users]);
};

export default useDataHealth;
